import {
  PRIORITIES,
  positionBetween,
  sprintStatus,
  toIsoDate,
  TASK_STATUSES,
  type Activity,
  type Game,
  type Label,
  type Member,
  type TaskComment,
  type TaskStatus,
} from '@hive/shared';
import { Bot, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useSprintName } from '@/cycles/sprints-context';
import { useSprints } from '@/data/cycles';
import { useAddDependency, useDependencies, useRemoveDependency } from '@/data/dependencies';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { useAddComment, useTaskActivity, useTaskComments } from '@/data/comments';
import { useCreateLabel } from '@/data/labels';
import {
  useCreateTask,
  useDeleteTask,
  useGameTasks,
  useToggleTaskLabel,
  useUpdateTask,
  type GridTask,
  type TaskPatch,
} from '@/data/tasks';
import { cn } from '@/lib/utils';
import { LinksPanel } from '@/vault/LinksPanel';
import { AttachmentDropZone } from '@/vault/UploadDialog';
import { activityActor, activitySummary } from './activity';
import { fromLocalDateTime, timeAgo, toLocalDateTime } from './format';
import { LabelChip } from './TaskBits';

/** The side panel for one task: every field, subtasks, labels, comments and history. */
export function TaskDrawer({
  task,
  subtasks,
  game,
  members,
  labels,
  onClose,
  onOpen,
}: {
  task: GridTask;
  subtasks: readonly GridTask[];
  game: Game;
  members: readonly Member[];
  labels: readonly Label[];
  onClose: () => void;
  onOpen: (taskId: string) => void;
}) {
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const [error, setError] = useState<string | null>(null);
  const panel = useRef<HTMLElement>(null);

  useEffect(() => {
    panel.current?.focus();
  }, [task.id]);

  const save = (patch: TaskPatch) => {
    setError(null);
    update.mutate(
      { id: task.id, gameId: task.game_id, patch },
      { onError: (e) => setError(e instanceof Error ? e.message : 'Could not save.') },
    );
  };

  return (
    <aside
      ref={panel}
      tabIndex={-1}
      role="dialog"
      aria-label={`Task: ${task.title}`}
      className="fixed top-0 right-0 bottom-0 z-40 flex w-[min(520px,100vw)] flex-col border-l border-magenta bg-bg-2 shadow-[0_0_32px_rgb(255_43_214/0.18)] outline-none"
    >
      <header className="flex items-start gap-2 border-b border-line p-4">
        <TitleField key={task.id} value={task.title} onSave={(title) => save({ title })} />
        <button
          type="button"
          className="cursor-pointer p-1 text-muted hover:text-text"
          onClick={onClose}
          aria-label="Close task"
        >
          <X className="size-5" aria-hidden />
        </button>
      </header>

      <AttachmentDropZone
        className="flex-1 space-y-6 overflow-y-auto p-4"
        context={{
          gameId: game.id,
          gameSlug: game.slug,
          gameName: game.name,
          module: 'Grid',
          attachTo: { type: 'task', id: task.id },
        }}
      >
        {error ? (
          <p role="alert" className="text-sm text-red">
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <Select value={task.status} onChange={(e) => save({ status: e.target.value as TaskStatus })}>
              {TASK_STATUSES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={task.priority} onChange={(e) => save({ priority: Number(e.target.value) })}>
              {PRIORITIES.map((p) => (
                <option key={p.id} value={p.id}>
                  P{p.id} {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Assignee">
            <Select value={task.assignee_id ?? ''} onChange={(e) => save({ assignee_id: e.target.value || null })}>
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Estimate (hours)">
            <EstimateField
              key={task.id}
              value={task.estimate_hours}
              onSave={(estimate_hours) => save({ estimate_hours })}
            />
          </Field>
          <SprintField task={task} gameId={game.id} onSave={(sprint_id) => save({ sprint_id })} />
          {task.status === 'done' && task.completed_at ? (
            <Field label="Completed" className="col-span-2">
              <Input
                key={task.completed_at}
                type="datetime-local"
                aria-label="Completed at"
                defaultValue={toLocalDateTime(task.completed_at)}
                max={toLocalDateTime(new Date().toISOString())}
                onBlur={(e) => {
                  const at = fromLocalDateTime(e.target.value);
                  if (at && at !== task.completed_at) save({ completed_at: at });
                }}
              />
            </Field>
          ) : null}
          <Field label="Start">
            <Input
              type="date"
              value={task.start_date ?? ''}
              onChange={(e) => save({ start_date: e.target.value || null })}
            />
          </Field>
          <Field label="Due">
            <Input
              type="date"
              value={task.due_date ?? ''}
              onChange={(e) => save({ due_date: e.target.value || null })}
            />
          </Field>
        </div>

        <TaskLabels task={task} game={game} labels={labels} />

        <Field label="Description (markdown)">
          <DescriptionField key={task.id} value={task.description} onSave={(description) => save({ description })} />
        </Field>

        <Subtasks parent={task} subtasks={subtasks} onOpen={onOpen} />

        <Dependencies key={`deps-${task.id}`} task={task} onOpen={onOpen} />

        <LinksPanel key={task.id} item={{ type: 'task', id: task.id }} />

        <Comments taskId={task.id} members={members} />

        <History taskId={task.id} members={members} />

        <ImportNote task={task} />

        <div className="border-t border-line pt-4">
          <Button
            variant="danger"
            size="sm"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete “${task.title}” and its subtasks and comments?`)) {
                remove.mutate(task.id, { onSuccess: onClose });
              }
            }}
          >
            <Trash2 aria-hidden /> Delete task
          </Button>
        </div>
      </AttachmentDropZone>
    </aside>
  );
}

function Dependencies({ task, onOpen }: { task: GridTask; onOpen: (taskId: string) => void }) {
  const dependencies = useDependencies();
  const gameTasks = useGameTasks(task.game_id);
  const add = useAddDependency();
  const remove = useRemoveDependency();
  const [error, setError] = useState<string | null>(null);
  const all = dependencies.data ?? [];
  const tasks = gameTasks.data ?? [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const blockers = all.filter((d) => d.blocked_id === task.id && byId.has(d.blocker_id));
  const blocking = all.filter((d) => d.blocker_id === task.id && byId.has(d.blocked_id));
  const linked = new Set([task.id, ...blockers.map((d) => d.blocker_id), ...blocking.map((d) => d.blocked_id)]);
  const candidates = tasks
    .filter((t) => !linked.has(t.id) && t.status !== 'done')
    .sort((a, b) => a.title.localeCompare(b.title));

  const addOne = (blockerId: string, blockedId: string) => {
    setError(null);
    add.mutate(
      { blockerId, blockedId },
      { onError: (e) => setError(e instanceof Error ? e.message : 'Could not add that.') },
    );
  };

  const list = (label: string, items: typeof all, other: (d: (typeof all)[number]) => string) => (
    <div className="flex flex-col gap-1">
      <span className="font-mono text-[11px] text-muted">{label}</span>
      <ul aria-label={label} className="flex flex-col gap-px">
        {items.map((d) => {
          const t = byId.get(other(d))!;
          return (
            <li key={d.id} className="flex items-center gap-2 bg-bg-3/60 px-2 py-1.5">
              <button
                type="button"
                className={cn(
                  'min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:text-cyan',
                  t.status === 'done' && 'text-muted line-through',
                )}
                onClick={() => onOpen(t.id)}
              >
                {t.title}
              </button>
              <button
                type="button"
                className="cursor-pointer p-0.5 text-muted hover:text-red"
                aria-label={`Remove: ${label.toLowerCase()} ${t.title}`}
                onClick={() => remove.mutate(d.id)}
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          );
        })}
        {items.length === 0 ? <li className="text-xs text-dim">None</li> : null}
      </ul>
    </div>
  );

  return (
    <section aria-label="Dependencies" className="flex flex-col gap-2">
      <h3 className="font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">Dependencies</h3>
      {list('Blocked by', blockers, (d) => d.blocker_id)}
      {list('Blocks', blocking, (d) => d.blocked_id)}
      <div className="grid grid-cols-2 gap-2">
        <Select
          aria-label="Add a task this waits on"
          value=""
          onChange={(e) => e.target.value && addOne(e.target.value, task.id)}
        >
          <option value="">Blocked by…</option>
          {candidates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Add a task that waits on this"
          value=""
          onChange={(e) => e.target.value && addOne(task.id, e.target.value)}
        >
          <option value="">Blocks…</option>
          {candidates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </Select>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-red">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function SprintField({
  task,
  gameId,
  onSave,
}: {
  task: GridTask;
  gameId: string;
  onSave: (sprintId: string | null) => void;
}) {
  const sprints = useSprints(gameId);
  const today = toIsoDate(new Date());
  // Closed sprints only show when the task is in one.
  const choices = (sprints.data ?? []).filter((s) => !s.closed_at || s.id === task.sprint_id);
  return (
    <Field label="Sprint" className="col-span-2">
      <Select
        value={task.sprint_id ?? ''}
        disabled={task.parent_id !== null}
        title={task.parent_id ? 'A subtask is in its parent’s sprint.' : undefined}
        onChange={(e) => onSave(e.target.value || null)}
      >
        <option value="">Backlog (no sprint)</option>
        {choices.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} · {sprintStatus(s, today)}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function TitleField({ value, onSave }: { value: string; onSave: (title: string) => void }) {
  const [draft, setDraft] = useState(value);
  const commit = () => {
    const title = draft.trim();
    if (title && title !== value) onSave(title);
    else setDraft(value);
  };
  return (
    <input
      className="min-w-0 flex-1 bg-transparent font-display text-lg leading-snug font-bold tracking-[0.04em] text-cyan outline-none focus:shadow-[inset_0_-1px_0_var(--color-cyan)]"
      value={draft}
      aria-label="Title"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

function EstimateField({ value, onSave }: { value: number | null; onSave: (hours: number | null) => void }) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  return (
    <Input
      type="number"
      min={0}
      step={0.5}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const hours = draft.trim() === '' ? null : Number(draft);
        if (hours !== value && (hours === null || (Number.isFinite(hours) && hours >= 0))) onSave(hours);
      }}
    />
  );
}

function DescriptionField({ value, onSave }: { value: string; onSave: (text: string) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <Textarea
      value={draft}
      rows={6}
      placeholder="What needs doing, in markdown."
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value) onSave(draft);
      }}
    />
  );
}

function TaskLabels({ task, game, labels }: { task: GridTask; game: Game; labels: readonly Label[] }) {
  const toggle = useToggleTaskLabel();
  const create = useCreateLabel();
  const [name, setName] = useState('');
  const on = labels.filter((l) => task.label_ids.includes(l.id));
  const off = labels.filter((l) => !task.label_ids.includes(l.id));

  async function addNew() {
    const trimmed = name.trim().replace(/^#/, '');
    if (!trimmed) return;
    const existing = labels.find((l) => l.name.toLowerCase() === trimmed.toLowerCase());
    const label = existing ?? (await create.mutateAsync({ name: trimmed, game_id: game.id }));
    if (!task.label_ids.includes(label.id)) toggle.mutate({ taskId: task.id, labelId: label.id, on: true });
    setName('');
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">Labels</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {on.map((l) => (
          <LabelChip
            key={l.id}
            label={l}
            onRemove={() => toggle.mutate({ taskId: task.id, labelId: l.id, on: false })}
          />
        ))}
        {on.length === 0 ? <span className="text-xs text-dim">None</span> : null}
      </div>
      <div className="flex gap-2">
        <Select
          aria-label="Add a label"
          value=""
          onChange={(e) => e.target.value && toggle.mutate({ taskId: task.id, labelId: e.target.value, on: true })}
        >
          <option value="">Add a label…</option>
          {off.map((l) => (
            <option key={l.id} value={l.id}>
              #{l.name}
              {l.game_id ? '' : ' (studio)'}
            </option>
          ))}
        </Select>
        <Input
          aria-label="New label"
          placeholder="New label"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addNew();
            }
          }}
        />
      </div>
    </div>
  );
}

function Subtasks({
  parent,
  subtasks,
  onOpen,
}: {
  parent: GridTask;
  subtasks: readonly GridTask[];
  onOpen: (taskId: string) => void;
}) {
  const create = useCreateTask();
  const update = useUpdateTask();
  const [title, setTitle] = useState('');
  const done = subtasks.filter((s) => s.status === 'done').length;

  function add() {
    const trimmed = title.trim();
    if (!trimmed) return;
    create.mutate(
      {
        game_id: parent.game_id,
        parent_id: parent.id,
        title: trimmed,
        position: positionBetween(subtasks.at(-1)?.position ?? null, null),
      },
      { onSuccess: () => setTitle('') },
    );
  }

  return (
    <section aria-label="Subtasks" className="flex flex-col gap-2">
      <h3 className="font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">
        Subtasks {subtasks.length > 0 ? `${done}/${subtasks.length}` : ''}
      </h3>
      <ul className="flex flex-col gap-px">
        {subtasks.map((s) => (
          <li key={s.id} className="flex items-center gap-2 bg-bg-3/60 px-2 py-1.5">
            <input
              type="checkbox"
              className="size-4 cursor-pointer accent-cyan"
              checked={s.status === 'done'}
              aria-label={s.status === 'done' ? `Reopen ${s.title}` : `Complete ${s.title}`}
              onChange={() =>
                update.mutate({ id: s.id, gameId: s.game_id, patch: { status: s.status === 'done' ? 'todo' : 'done' } })
              }
            />
            <button
              type="button"
              className={cn(
                'flex-1 cursor-pointer text-left text-sm hover:text-cyan',
                s.status === 'done' && 'text-muted line-through',
              )}
              onClick={() => onOpen(s.id)}
            >
              {s.title}
            </button>
          </li>
        ))}
      </ul>
      <Input
        aria-label="New subtask"
        placeholder="Add a subtask"
        value={title}
        disabled={create.isPending}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
      />
    </section>
  );
}

function Comments({ taskId, members }: { taskId: string; members: readonly Member[] }) {
  const comments = useTaskComments(taskId);
  const add = useAddComment();
  const [body, setBody] = useState('');

  return (
    <section aria-label="Comments" className="flex flex-col gap-2">
      <h3 className="font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">Comments</h3>
      <CommentList comments={comments.data ?? []} members={members} />
      <Textarea
        aria-label="New comment"
        placeholder="Write a comment (markdown). Ctrl+Enter to post."
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && body.trim()) {
            add.mutate({ taskId, body: body.trim() }, { onSuccess: () => setBody('') });
          }
        }}
      />
      <div>
        <Button
          size="sm"
          disabled={!body.trim() || add.isPending}
          onClick={() => add.mutate({ taskId, body: body.trim() }, { onSuccess: () => setBody('') })}
        >
          Comment
        </Button>
      </div>
    </section>
  );
}

export function CommentList({ comments, members }: { comments: readonly TaskComment[]; members: readonly Member[] }) {
  if (comments.length === 0) return <p className="text-xs text-dim">No comments yet.</p>;
  return (
    <ul className="flex flex-col gap-3">
      {comments.map((c) => {
        const author = members.find((m) => m.id === c.author_id);
        const importedFrom = (c.import_meta as { author_name?: string } | null)?.author_name;
        const name = author?.display_name ?? importedFrom ?? 'Unknown';
        return (
          <li key={c.id} className="flex gap-2">
            <MemberAvatar name={name} avatarUrl={author?.avatar_url ?? null} className="size-6 text-[9px]" />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-xs">
                <span className="font-semibold">{name}</span>
                {author?.kind === 'claude' ? <ClaudeBadge /> : null}
                <span className="text-dim">{timeAgo(c.created_at)}</span>
                {c.source ? <span className="text-dim">from {c.source}</span> : null}
              </p>
              <p className="text-sm whitespace-pre-wrap">{c.body}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ClaudeBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-violet/15 px-1 font-mono text-[10px] text-violet">
      <Bot className="size-3" aria-hidden />
      Claude
    </span>
  );
}

function History({ taskId, members }: { taskId: string; members: readonly Member[] }) {
  const activity = useTaskActivity(taskId);
  return (
    <section aria-label="History" className="flex flex-col gap-2">
      <h3 className="font-display text-[9px] font-bold tracking-[0.18em] text-muted uppercase">History</h3>
      <ActivityList entries={activity.data ?? []} members={members} />
    </section>
  );
}

export function ActivityList({ entries, members }: { entries: readonly Activity[]; members: readonly Member[] }) {
  const sprintName = useSprintName();
  if (entries.length === 0) return <p className="text-xs text-dim">Nothing yet.</p>;
  return (
    <ol className="flex flex-col gap-1.5">
      {entries.map((e) => (
        <li key={e.id} className="flex items-baseline gap-2 text-xs">
          {e.author_kind === 'claude' ? <ClaudeBadge /> : null}
          <span>
            <span className="font-semibold">{activityActor(e, members)}</span>{' '}
            <span className="text-muted">{activitySummary(e, members, sprintName)}</span>
          </span>
          <span className="ml-auto shrink-0 text-dim">{timeAgo(e.created_at)}</span>
        </li>
      ))}
    </ol>
  );
}

/** Imported tasks remember who Todoist had them assigned to until that person joins. */
function ImportNote({ task }: { task: GridTask }) {
  if (!task.source) return null;
  const meta = (task.import_meta ?? {}) as { assignee_email?: string; assignee_pending?: boolean };
  return (
    <p className="font-mono text-[11px] text-dim">
      Imported from {task.source} ({task.source_id})
      {meta.assignee_pending && meta.assignee_email && !task.assignee_id
        ? `; assigned there to ${meta.assignee_email}, who hasn't joined Hive yet`
        : ''}
      .
    </p>
  );
}
